import { clamp } from '../shared/track.js';

export interface DeviceProbe {
  maxTouchPoints: number;
  coarsePointer: boolean;
  shortEdge: number;
  userAgent: string;
  platform: string;
  motionApi: boolean;
}

export interface DeviceCapabilities {
  touchDevice: boolean;
  phone: boolean;
  tablet: boolean;
  motionSteering: boolean;
}

export interface MotionRuntimeProbe {
  secureContext: boolean;
  orientationApi: boolean;
  accelerationApi: boolean;
  userAgent: string;
}

export interface MotionRuntimeStatus extends MotionRuntimeProbe {
  available: boolean;
  legacyChromium: boolean;
  state: 'ready' | 'insecure' | 'missing';
}

export function detectDeviceCapabilities(): DeviceCapabilities {
  const screenWidth = window.screen?.width || window.innerWidth;
  const screenHeight = window.screen?.height || window.innerHeight;
  return classifyDevice({
    maxTouchPoints: navigator.maxTouchPoints || 0,
    coarsePointer: window.matchMedia('(pointer: coarse)').matches,
    shortEdge: Math.min(screenWidth, screenHeight),
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    motionApi: hasMotionSensorApi(window),
  });
}

/** Older Chromium/WebView builds sometimes expose only the event-handler
 * property, without a constructible DeviceMotionEvent global. */
export function hasMotionSensorApi(runtime: object): boolean {
  return 'DeviceOrientationEvent' in runtime
    || 'DeviceMotionEvent' in runtime
    || 'ondeviceorientation' in runtime
    || 'ondeviceorientationabsolute' in runtime
    || 'ondevicemotion' in runtime;
}

export function classifyMotionRuntime(probe: MotionRuntimeProbe): MotionRuntimeStatus {
  const match = probe.userAgent.match(/(?:Chrome|Chromium)\/(\d+)/i);
  const chromiumMajor = Number(match?.[1]);
  const legacyChromium = Number.isFinite(chromiumMajor) && chromiumMajor > 0 && chromiumMajor < 110;
  const available = probe.orientationApi || probe.accelerationApi;
  return {
    ...probe,
    available,
    legacyChromium,
    state: !probe.secureContext ? 'insecure' : available ? 'ready' : 'missing',
  };
}

export function detectMotionRuntime(): MotionRuntimeStatus {
  return classifyMotionRuntime({
    secureContext: window.isSecureContext,
    orientationApi: 'DeviceOrientationEvent' in window
      || 'ondeviceorientation' in window
      || 'ondeviceorientationabsolute' in window,
    accelerationApi: 'DeviceMotionEvent' in window || 'ondevicemotion' in window,
    userAgent: navigator.userAgent,
  });
}

export function classifyDevice(probe: DeviceProbe): DeviceCapabilities {
  const mobileUserAgent = /Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(probe.userAgent);
  const ipadDesktopMode = probe.platform === 'MacIntel' && probe.maxTouchPoints > 1;
  const touchDevice = probe.maxTouchPoints > 0
    && (mobileUserAgent || ipadDesktopMode || (probe.coarsePointer && probe.shortEdge <= 900));
  const androidTablet = /Android/i.test(probe.userAgent) && !/Mobile/i.test(probe.userAgent);
  const explicitTablet = /iPad|Tablet/i.test(probe.userAgent) || androidTablet || ipadDesktopMode;
  const phone = touchDevice && !explicitTablet && probe.shortEdge <= 600;
  const tablet = touchDevice && !phone;
  return {
    touchDevice,
    phone,
    tablet,
    // Sensor steering is useful on both phones and tablets. The previous
    // phone-only gate made Android tablets fall back to touch even when Chrome
    // exposed perfectly usable orientation or accelerometer data.
    motionSteering: touchDevice && probe.motionApi,
  };
}

export function isLegacyTouchBrowser(
  capabilities: DeviceCapabilities,
  userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent,
): boolean {
  if (!capabilities.touchDevice) return false;
  const match = userAgent.match(/(?:Chrome|Chromium)\/(\d+)/i);
  const chromiumMajor = Number(match?.[1]);
  return Number.isFinite(chromiumMajor) && chromiumMajor > 0 && chromiumMajor < 110;
}

export function screenTiltDegrees(beta: number, gamma: number, orientationAngle: number): number {
  const normalizedAngle = ((orientationAngle % 360) + 360) % 360;
  if (normalizedAngle >= 45 && normalizedAngle < 135) return -beta;
  if (normalizedAngle >= 225 && normalizedAngle < 315) return beta;
  if (normalizedAngle >= 135 && normalizedAngle < 225) return -gamma;
  return gamma;
}

/** Positive steering means left in the shared control convention, therefore
 * its dashboard indicator moves toward the left side of the screen. */
export function motionIndicatorOffsetPx(steer: number): number {
  return -clamp(steer, -1, 1) * 8;
}

export type OrientationMotionAxis = 'beta' | 'gamma';

/**
 * Some older Android WebViews report the steering motion on the opposite
 * orientation axis. Stay on the screen-standard axis until enough samples are
 * available, then switch only when the alternate axis clearly carries motion.
 */
export function preferredOrientationAxis(
  orientationAngle: number,
  betaSpan: number,
  gammaSpan: number,
  samples: number,
): OrientationMotionAxis {
  const angle = normalizeAngle(orientationAngle);
  const expected: OrientationMotionAxis = (angle >= 45 && angle < 135) || (angle >= 225 && angle < 315)
    ? 'beta'
    : 'gamma';
  if (samples < 6) return expected;
  const expectedSpan = expected === 'beta' ? betaSpan : gammaSpan;
  const alternateSpan = expected === 'beta' ? gammaSpan : betaSpan;
  return alternateSpan >= 4.5 && alternateSpan > Math.max(2.5, expectedSpan * 1.8)
    ? expected === 'beta' ? 'gamma' : 'beta'
    : expected;
}

export function orientationAxisTiltDegrees(
  beta: number,
  gamma: number,
  orientationAngle: number,
  axis: OrientationMotionAxis,
): number {
  const angle = normalizeAngle(orientationAngle);
  const landscape = (angle >= 45 && angle < 135) || (angle >= 225 && angle < 315);
  if ((landscape && axis === 'beta') || (!landscape && axis === 'gamma')) {
    return screenTiltDegrees(beta, gamma, angle);
  }
  const value = axis === 'beta' ? beta : gamma;
  const sign = (angle >= 45 && angle < 225) ? -1 : 1;
  return value * sign;
}

export function effectiveScreenOrientationAngle(
  screenAngle: number | undefined,
  orientationType: string | undefined,
  landscape: boolean,
  legacyAngle?: number,
): number {
  const normalized = normalizeAngle(screenAngle ?? 0);
  if (!landscape || normalized % 180 !== 0) return normalized;
  const normalizedLegacy = Number.isFinite(legacyAngle) ? normalizeAngle(Number(legacyAngle)) : null;
  if (normalizedLegacy !== null && normalizedLegacy % 180 === 90) return normalizedLegacy;
  return orientationType === 'landscape-secondary' ? 270 : 90;
}

export function steeringFromTilt(tilt: number, neutral: number, sensitivity: number): number {
  const fullLockDegrees = 24 / Math.sqrt(clamp(sensitivity, 0.6, 1.8));
  const delta = signedDegreeDifference(tilt, neutral);
  const deadZone = 2;
  if (Math.abs(delta) <= deadZone) return 0;
  const normalized = clamp(
    (Math.abs(delta) - deadZone) / Math.max(8, fullLockDegrees - deadZone),
    0,
    1,
  );
  // A progressive response keeps small corrections genuinely small while
  // preserving full lock for hairpins. The old linear/high-sensitivity curve
  // reached full lock after roughly 15 degrees and felt almost binary.
  const curved = normalized ** 1.3;
  // The screen-relative sensor helpers already normalize the physical roll
  // direction to the game's chase-camera steering convention. Keep that sign
  // here so tilting toward a corner turns toward it on every sensor fallback.
  return Math.sign(delta) * curved;
}

/** Low-pass filters sensor noise without breaking at the +180/-180 boundary. */
export function smoothTiltDegrees(current: number, target: number, deltaSeconds: number): number {
  if (!Number.isFinite(target)) return current;
  if (!Number.isFinite(current)) return target;
  const dt = clamp(deltaSeconds, 0, 0.12);
  const blend = 1 - Math.exp(-dt * 10);
  return current + signedDegreeDifference(target, current) * blend;
}

/**
 * Converts the gravity vector exposed by legacy `devicemotion` events into a
 * screen-relative roll angle. This is a fallback for older Android Chrome
 * builds/tablets that expose an accelerometer but never deliver orientation
 * events. Neutral calibration removes the device's natural holding angle.
 */
export function accelerationTiltDegrees(
  x: number,
  y: number,
  z: number,
  orientationAngle: number,
): number {
  if (![x, y, z].every(Number.isFinite)) return Number.NaN;
  const angle = normalizeAngle(orientationAngle);
  let horizontal = x;
  let remaining = y;
  if (angle >= 45 && angle < 135) {
    horizontal = -y;
    remaining = x;
  } else if (angle >= 135 && angle < 225) {
    horizontal = -x;
    remaining = -y;
  } else if (angle >= 225 && angle < 315) {
    horizontal = y;
    remaining = -x;
  }
  return Math.atan2(horizontal, Math.max(0.001, Math.hypot(remaining, z))) * (180 / Math.PI);
}

function normalizeAngle(angle: number): number {
  return ((angle % 360) + 360) % 360;
}

function signedDegreeDifference(target: number, current: number): number {
  return ((target - current + 540) % 360) - 180;
}
